import type { RouteRecordRaw } from 'vue-router'
import { ref, useEventListener, watch } from '@/composition/vue/compositionApi'
import { getCookieByName, setCookieByName } from '@/utils/cookies'
import { delay } from '@/utils/system'
import { useBreakpoints } from '@/composition/breakpoints'

export type UseFeatureTooltipParams = {
  key: string
  openDelay: number
  remindDays: number
}

export type FeatureTooltipConfig = UseFeatureTooltipParams & {
  component: RouteRecordRaw['component']
}

const closedCookie = 'closed'
const closedForeverCookie = 'closed-forever'

function getCookieNameForKey(key: string) {
  return `ftooltip--${key}`
}

export function useFeatureTooltip({ key, openDelay, remindDays }: UseFeatureTooltipParams) {
  const { isSmBreakpoint } = useBreakpoints()
  const cookieName = getCookieNameForKey(key)
  const isOpen = ref(false)

  const open = async (): Promise<void> => {
    if (!navigator.cookieEnabled || getCookieByName(cookieName) !== null || isSmBreakpoint.value) {
      return
    }

    await delay(openDelay / 1000)
    isOpen.value = true

    // compensate animation time
    await delay(0.2)
  }

  const close = () => {
    setCookieByName(cookieName, closedCookie, remindDays)
    isOpen.value = false
  }

  const hide = () => {
    isOpen.value = false
  }

  const closeForever = () => {
    // https://developer.chrome.com/blog/cookie-max-age-expires
    setCookieByName(cookieName, closedForeverCookie, 400)
    isOpen.value = false
  }

  watch(
    isSmBreakpoint,
    (val) => {
      if (val) {
        hide()
      }
    },
    {
      immediate: true,
    },
  )

  return {
    isOpen,
    open,
    close,
    closeForever,
  }
}

export const useFeatureTooltipBackClick = (close: () => void) => {
  const contentRef = ref<HTMLDivElement>()
  const clickHandler = (event: MouseEvent) => {
    if (!(event.target instanceof HTMLElement) || contentRef.value?.contains(event.target)) {
      return
    }
    close()
    listenerCleanup()
  }
  const listenerCleanup = useEventListener(document, 'click', clickHandler, { capture: true })

  return {
    contentRef,
    listenerCleanup,
  }
}

export const useFeatureTooltipDisplay = (config: UseFeatureTooltipParams) => {
  const cookieName = getCookieNameForKey(config.key)
  const isFeatureTooltipDisplayed = !getCookieByName(cookieName)
  const isClosedForever = () => {
    return getCookieByName(cookieName) === closedForeverCookie
  }
  if (isClosedForever()) {
    // bypass 400 days limitation
    setCookieByName(cookieName, closedForeverCookie, 400)
  }

  return {
    isFeatureTooltipDisplayed,
    isClosedForever,
  }
}
